import { PaginationData } from '../models/monitor/data/sources/PaginationData';

export interface PaginationEvent {
  first: number;
  rows: number;
}

export interface PaginationValues {
  first: number;
  rows: number;
  totalRecords: number;
}

/**
 * Calculates pagination values (first, rows, totalRecords) from pagination data
 */
export const calculatePaginationValues = (
  pagination: PaginationData | undefined,
  localDataLength: number
): PaginationValues => {
  const first = pagination ? (pagination.currentPage - 1) * pagination.pageSize : 0;
  const rows = pagination?.pageSize ?? 10;
  const totalRecords = pagination?.totalItems ?? localDataLength;

  return { first, rows, totalRecords };
};

/**
 * Creates a safe first value that ensures it's never out of bounds
 */
export const calculateSafeFirst = (first: number, rows: number, totalRecords: number): number => {
  if (totalRecords === 0) {
    return 0;
  }

  const maxFirst = Math.floor((totalRecords - 1) / rows) * rows;
  return Math.max(0, Math.min(first, maxFirst));
};

/**
 * Converts PrimeReact pagination event to page number and page size
 */
export const extractPageFromEvent = (event: PaginationEvent): { page: number; pageSize: number } => {
  const page = Math.floor(event.first / event.rows) + 1;
  const pageSize = event.rows;
  return { page, pageSize };
};

/**
 * Creates a pagination change handler that only triggers when values actually change
 */
export const createPaginationHandler = (
  pagination: PaginationData | undefined,
  onPageChange: (page: number, pageSize: number) => void
) => {
  return (event: PaginationEvent) => {
    const { page: newPage, pageSize: newPageSize } = extractPageFromEvent(event);

    // Only trigger page change if values actually changed
    if (pagination) {
      const currentPage = pagination.currentPage;
      const currentPageSize = pagination.pageSize;

      if (newPage !== currentPage || newPageSize !== currentPageSize) {
        onPageChange(newPage, newPageSize);
      }
    } else {
      onPageChange(newPage, newPageSize);
    }
  };
};

/**
 * Determines if local filtering should be disabled when server-side pagination is active
 */
export const shouldDisableLocalFiltering = (pagination: PaginationData | undefined): boolean => {
  return !!pagination;
};
